import { Component, computed, signal, OnInit, OnDestroy, inject, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { SafeHtml } from '@angular/platform-browser';
import { NotificationService, Notification } from '../../../core/services/notifications/notification.service';
import {
  HtmlProcessingService,
  ensureFileNameHasExtension,
  getFileExtensionFromNameOrUrl,
} from '@app/core/services/html-processing/html-processing.service';
import {
  NotificationAttachmentPreviewService,
  NotificationAttachmentKind,
} from '@app/core/services/notification-attachment-preview/notification-attachment-preview.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Spinner } from '../spinner/spinner';
import { Router } from '@angular/router';
import { RelativeTimePipe } from '@app/shared/components/pipes/time-pipes';

@Component({
  selector: 'app-notifications',
  templateUrl: './notifications.html',
  styleUrls: ['./notifications.scss'],
  standalone: true,
  imports: [
    CommonModule,
    Spinner,
    RelativeTimePipe,
  ]
})
export class NotificationsComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private readonly htmlProcessing = inject(HtmlProcessingService);
  private readonly host = inject(ElementRef<HTMLElement>);
  readonly attachmentPreview = inject(NotificationAttachmentPreviewService);
  private readonly descriptionHtmlCache = new Map<string, SafeHtml>();
  private readonly onPanelClick = (event: MouseEvent) => this.onDescriptionClick(event);
  
  activeTab = signal<'whats-new' | 'previous'>('whats-new');
  
  // Track image loading errors
  imageLoadError: { [key: number]: boolean } = {};
  
  // Use service signals (initialized after constructor)
  allNotifications = computed(() => this.notificationService.notifications());
  unreadCount = computed(() => this.notificationService.unreadCount());
  isLoading = computed(() => this.notificationService.isLoading());
  isInitialized = computed(() => this.notificationService.isInitialized());
  
  // Filtered notifications based on active tab
  notifications = computed(() => {
    const allNotifs = this.allNotifications();
    const tab = this.activeTab();
    
    let filtered: Notification[];
    
    if (tab === 'whats-new') {
      // Show unread notifications (What's New)
      filtered = allNotifs.filter(n => !n.isRead);
    } else {
      // Show read notifications (Previous)
      filtered = allNotifs.filter(n => n.isRead);
    }
    
    // Sort: Pinned notifications first, then by created date (newest first)
    return filtered.sort((a, b) => {
      // First, sort by pinned status
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      
      // If both have same pinned status, sort by creation date (newest first)
      const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      return dateB - dateA;
    });
  });

  constructor(public notificationService: NotificationService, private router: Router) {
  }

  ngOnInit(): void {
    // Notify service that panel is open
    this.notificationService.setPanelOpen(true);
    this.host.nativeElement.addEventListener('click', this.onPanelClick, true);

    // Listen for new notifications
    this.notificationService.newNotification$
      .pipe(takeUntil(this.destroy$))
      .subscribe(notification => {
        console.log('NotificationsComponent: New notification received:', notification);
      });
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onPanelClick, true);
    // Notify service that panel is closed
    this.notificationService.setPanelOpen(false);
    
    // Clean up
    this.destroy$.next();
    this.destroy$.complete();
  }

  setActiveTab(tab: 'whats-new' | 'previous') {
    this.activeTab.set(tab);
  }

  clearAll() {
    // Clear all notifications in the current tab
    const currentTab = this.activeTab();
    const notificationsToUpdate = this.notifications();
    
    if (currentTab === 'whats-new') {
      // Mark all unread notifications as read
      notificationsToUpdate.forEach(notification => {
        this.notificationService.markAsRead(notification.id);
      });
    } else {
      // For previous tab, we could implement deletion logic
      // For now, just mark them all as read (they're already read)
      console.log('Previous notifications cleared');
    }
  }

  togglePin(notification: Notification) {
    this.notificationService.togglePin(notification.id);
  }

  getInitials(name: string): string {
    if (!name) {
      return '?';
    }
    return name
      .split(' ')
      .filter(Boolean)
      .map(part => part[0])
      .join('')
      .substring(0, 2)
      .toUpperCase();
  }

  getAvatarClasses(index: number, hasAvatar: boolean): Record<string, boolean> {
    const classes: Record<string, boolean> = {
      'user-avatar': true,
      'has-avatar': hasAvatar,
    };

    if (!hasAvatar) {
      const paletteIndex = (index % 6) + 1;
      classes['avatar-placeholder'] = true;
      classes[`avatar-palette-${paletteIndex}`] = true;
    }

    return classes;
  }

  markAsRead(notification: Notification) {
    this.notificationService.markAsRead(notification.id);
  }


  // Handle image loading errors
  onImageError(index: number): void {
    this.imageLoadError[index] = true;
  }

  // Handle successful image loading
  onImageLoad(index: number): void {
    this.imageLoadError[index] = false;
  }

  /** API HTML; linkify plain URLs, chip images, truncate, keep anchors clickable. */
  getSafeDescription(description: string | undefined): SafeHtml {
    const raw = description ?? '';
    const cached = this.descriptionHtmlCache.get(raw);
    if (cached) {
      return cached;
    }

    const safe = this.htmlProcessing.getSafeHtml(this.prepareDescriptionHtml(raw));
    if (this.descriptionHtmlCache.size > 150) {
      const firstKey = this.descriptionHtmlCache.keys().next().value;
      if (firstKey != null) {
        this.descriptionHtmlCache.delete(firstKey);
      }
    }
    this.descriptionHtmlCache.set(raw, safe);
    return safe;
  }

  onDescriptionClick(event: MouseEvent): void {
    const description = (event.target as HTMLElement | null)?.closest?.('.notification-description');
    if (!description || !this.host.nativeElement.contains(description)) {
      return;
    }

    const anchor = (event.target as HTMLElement | null)?.closest?.('a');
    if (!anchor || !description.contains(anchor)) {
      return;
    }

    const href = this.usableHref(anchor);
    if (!href) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.handleAttachmentOrLink(anchor, href);
  }

  /** Only open when the user actually clicked an anchor — never the whole description. */
  private handleAttachmentOrLink(anchor: Element, href: string): void {
    const fileName = this.fileNameFromAnchor(anchor, href);
    const preferred = this.preferredKindFromAnchor(anchor);
    const resolved = ensureFileNameHasExtension(fileName, href);
    const kind =
      preferred ||
      this.attachmentPreview.detectKind(resolved, href, fileName);

    if (kind === 'image' || kind === 'media') {
      this.attachmentPreview.openAttachment(href, resolved, kind);
      return;
    }

    // Other downloadable files (pdf, docs, etc.) — keep extension like documents.
    const ext = getFileExtensionFromNameOrUrl(resolved, href);
    if (ext && !this.isWebPageExtension(ext)) {
      this.attachmentPreview.openAttachment(href, resolved, 'file');
      return;
    }

    this.openExternalHref(href);
  }

  private preferredKindFromAnchor(anchor: Element): NotificationAttachmentKind | undefined {
    if (
      anchor.classList.contains('notification-image-chip') ||
      anchor.classList.contains('entry-uploaded-image')
    ) {
      return 'image';
    }
    if (anchor.classList.contains('notification-media-chip')) {
      return 'media';
    }
    return undefined;
  }

  private fileNameFromAnchor(anchor: Element, href: string): string {
    const dataName = (anchor.getAttribute('data-file-name') || '').trim();
    const chipName =
      anchor.querySelector('.notification-image-chip__name, .notification-media-chip__name, .entry-uploaded-image__name')
        ?.textContent || '';
    const title = (anchor.getAttribute('title') || '').trim();
    const text = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
    return (
      dataName ||
      chipName.trim() ||
      title ||
      text ||
      this.basenameFromHref(href) ||
      'download'
    );
  }

  private isWebPageExtension(ext: string): boolean {
    return ['html', 'htm', 'php', 'asp', 'aspx'].includes((ext || '').toLowerCase());
  }

  private usableHref(anchor: Element | null): string {
    if (!anchor) {
      return '';
    }
    const href = (anchor.getAttribute('href') || '').trim();
    if (!href || href === '#' || href.toLowerCase().startsWith('javascript:')) {
      return '';
    }
    return href;
  }

  private openExternalHref(href: string): void {
    const url = this.resolveDescriptionHref(href);
    const opener = document.createElement('a');
    opener.href = url;
    opener.target = '_blank';
    opener.rel = 'noopener noreferrer';
    document.body.appendChild(opener);
    opener.click();
    opener.remove();
  }

  /**
   * Prep notification HTML for the compact panel:
   * - turn uploaded images into small chips (so they don't hide text or wrap the whole body)
   * - unwrap anchors that swallowed surrounding text around an image
   * - truncate to a short preview
   */
  private prepareDescriptionHtml(html: string): string {
    const linked = this.htmlProcessing.linkifyPlainUrlsInHtml(html || '');
    if (typeof DOMParser === 'undefined') {
      return this.htmlProcessing.truncateHtmlByWords(linked, 40);
    }

    try {
      const doc = new DOMParser().parseFromString(linked, 'text/html');

      doc.querySelectorAll('video, iframe, object, embed').forEach((el) => {
        if (el instanceof HTMLVideoElement) {
          const src =
            (el.getAttribute('src') || el.querySelector('source')?.getAttribute('src') || '').trim();
          const name =
            (el.getAttribute('title') || el.getAttribute('aria-label') || '').trim() ||
            this.basenameFromHref(src) ||
            'Video';
          if (src) {
            el.replaceWith(this.createMediaChip(doc, src, name));
            return;
          }
        }
        el.remove();
      });

      doc.querySelectorAll('img').forEach((imgEl) => {
        const img = imgEl as HTMLImageElement;
        const src = (img.getAttribute('src') || '').trim();
        const alt = (img.getAttribute('alt') || '').trim() || this.basenameFromHref(src) || 'Image';
        const chip = this.createImageChip(doc, src || '#', alt);

        const parentAnchor = img.closest('a');
        img.replaceWith(chip);

        // Image was inside a link — unwrap so the link does not cover the rest of the text.
        if (parentAnchor && parentAnchor !== chip && doc.body.contains(parentAnchor)) {
          const chipHref =
            this.usableHref(parentAnchor) ||
            this.usableHref(chip) ||
            src;
          if (chipHref) {
            chip.setAttribute('href', this.resolveDescriptionHref(chipHref));
          }
          const nodes = Array.from(parentAnchor.childNodes);
          parentAnchor.replaceWith(...nodes);
        }
      });

      // Turn video/audio file anchors into compact media chips.
      doc.querySelectorAll('a[href]').forEach((anchor) => {
        if (
          anchor.classList.contains('notification-image-chip') ||
          anchor.classList.contains('notification-media-chip') ||
          anchor.classList.contains('entry-uploaded-image')
        ) {
          return;
        }
        const href = (anchor.getAttribute('href') || '').trim();
        if (!href) {
          return;
        }
        const label = (anchor.textContent || '').replace(/\s+/g, ' ').trim();
        const resolved = ensureFileNameHasExtension(
          label || this.basenameFromHref(href) || 'download',
          href
        );
        if (this.attachmentPreview.detectKind(resolved, href, label) === 'media') {
          anchor.replaceWith(this.createMediaChip(doc, href, resolved));
        }
      });

      // Anchors that are only an image chip already — leave as-is.
      // Anchors wrapping large blocks of text + chip: unwrap text, keep chip linked.
      doc.querySelectorAll('a[href]').forEach((anchor) => {
        const href = (anchor.getAttribute('href') || '').trim();
        if (!href) {
          return;
        }

        const isChip =
          anchor.classList.contains('notification-image-chip') ||
          anchor.classList.contains('notification-media-chip');
        const textLength = (anchor.textContent || '').replace(/\s+/g, ' ').trim().length;
        const hasBlockKids = !!anchor.querySelector('p, div, li, ul, ol, h1, h2, h3, h4, table');

        if (!isChip && (hasBlockKids || textLength > 80)) {
          // Likely a "whole content" wrapper — unwrap so text is not one giant clickable region.
          const nodes = Array.from(anchor.childNodes);
          const hasChip = nodes.some(
            (n) =>
              n instanceof HTMLElement &&
              (n.classList.contains('notification-image-chip') ||
                n.classList.contains('notification-media-chip'))
          );
          if (hasChip || hasBlockKids) {
            anchor.replaceWith(...nodes);
            return;
          }
        }

        anchor.setAttribute('href', this.resolveDescriptionHref(href));
        anchor.setAttribute('target', '_blank');
        anchor.setAttribute('rel', 'noopener noreferrer');
      });

      this.truncateDescriptionDom(doc.body, 40);
      return doc.body.innerHTML;
    } catch {
      return this.htmlProcessing.truncateHtmlByWords(linked, 40);
    }
  }

  /** Keep early text + chips/links; drop the rest after `wordLimit` words. */
  private truncateDescriptionDom(root: HTMLElement, wordLimit: number): void {
    let wordsUsed = 0;
    let done = false;
    const toRemove: Node[] = [];

    const visit = (node: Node): void => {
      if (done) {
        toRemove.push(node);
        return;
      }

      if (node.nodeType === Node.TEXT_NODE) {
        const raw = node.textContent || '';
        const parts = raw.split(/(\s+)/);
        let rebuilt = '';
        for (const part of parts) {
          if (!part) {
            continue;
          }
          if (/^\s+$/.test(part)) {
            if (wordsUsed > 0 && wordsUsed < wordLimit) {
              rebuilt += part;
            }
            continue;
          }
          if (wordsUsed >= wordLimit) {
            done = true;
            break;
          }
          wordsUsed += 1;
          rebuilt += part;
        }
        if (done) {
          node.textContent = rebuilt ? `${rebuilt}…` : '…';
          return;
        }
        node.textContent = rebuilt;
        return;
      }

      if (!(node instanceof HTMLElement)) {
        return;
      }

      // Always keep compact image/media chips even after the word budget if they appear early;
      // once over budget, drop trailing chips with the rest.
      if (
        node.classList.contains('notification-image-chip') ||
        node.classList.contains('notification-media-chip')
      ) {
        if (wordsUsed >= wordLimit) {
          toRemove.push(node);
        }
        return;
      }

      Array.from(node.childNodes).forEach(visit);
    };

    Array.from(root.childNodes).forEach(visit);
    toRemove.forEach((n) => n.parentNode?.removeChild(n));
  }

  private createImageChip(doc: Document, href: string, fileName: string): HTMLAnchorElement {
    const a = doc.createElement('a');
    a.className = 'notification-image-chip';
    a.setAttribute('href', this.resolveDescriptionHref(href));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('title', fileName);
    a.setAttribute('data-file-name', ensureFileNameHasExtension(fileName, href));
    const icon = doc.createElement('i');
    icon.className = 'wl_icon_img-icon notification-image-chip__icon';
    icon.setAttribute('aria-hidden', 'true');
    const name = doc.createElement('span');
    name.className = 'notification-image-chip__name';
    name.textContent = this.shortFileName(ensureFileNameHasExtension(fileName, href));
    a.appendChild(icon);
    a.appendChild(name);
    return a;
  }

  private createMediaChip(doc: Document, href: string, fileName: string): HTMLAnchorElement {
    const resolved = ensureFileNameHasExtension(fileName, href);
    const a = doc.createElement('a');
    a.className = 'notification-media-chip';
    a.setAttribute('href', this.resolveDescriptionHref(href));
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    a.setAttribute('title', resolved);
    a.setAttribute('data-file-name', resolved);
    const name = doc.createElement('span');
    name.className = 'notification-media-chip__name';
    name.textContent = this.shortFileName(resolved);
    a.appendChild(name);
    return a;
  }

  private shortFileName(name: string): string {
    const cleaned = (name || 'Image').trim() || 'Image';
    if (cleaned.length <= 22) {
      return cleaned;
    }
    const extMatch = cleaned.match(/(\.[a-z0-9]{2,5})$/i);
    const ext = extMatch?.[1] || '';
    const base = ext ? cleaned.slice(0, -ext.length) : cleaned;
    return `${base.slice(0, 16)}…${ext}`;
  }

  private basenameFromHref(href: string): string {
    if (!href) {
      return '';
    }
    try {
      const u = new URL(href, window.location.origin);
      return decodeURIComponent(u.pathname.split('/').filter(Boolean).pop() || '') || '';
    } catch {
      const parts = href.split(/[/?#]/).filter(Boolean);
      return parts.length ? decodeURIComponent(parts[parts.length - 1]) : '';
    }
  }

  private resolveDescriptionHref(href: string): string {
    const trimmed = href.trim();
    if (/^(https?:|mailto:|tel:)/i.test(trimmed)) {
      return trimmed;
    }
    if (trimmed.startsWith('//')) {
      return `${window.location.protocol}${trimmed}`;
    }
    if (trimmed.startsWith('/#/')) {
      return `${window.location.origin}${trimmed}`;
    }
    if (trimmed.startsWith('#/')) {
      return `${window.location.origin}/${trimmed}`;
    }
    if (trimmed.startsWith('/')) {
      return `${window.location.origin}/#${trimmed}`;
    }
    return trimmed;
  }

  getDisplayName(notification: Notification): string {
    // If objectDetails exists, use the name from it
    if (notification.objectDetails) {
      // For PROJECT type, use projectName
      if (notification.objCode === 'PROJECT' && notification.objectDetails.projectName) {
        return notification.objectDetails.projectName;
      }
      // For TASK type, use taskName
      if (notification.objCode === 'TASK' && notification.objectDetails.taskName) {
        return notification.objectDetails.taskName;
      }
      // For PEOPLE type, use personName
      if (notification.objCode === 'PEOPLE' && notification.objectDetails.personName) {
        return notification.objectDetails.personName;
      }
      // For TIMESHEET type, use timesheetName
      if (notification.objCode === 'TIMESHEET' && notification.objectDetails.timesheetName) {
        return notification.objectDetails.timesheetName;
      }
      // For REVISION type, use revisionName
      if (notification.objCode === 'REVISION' && notification.objectDetails.revisionName) {
        return notification.objectDetails.revisionName;
      }
      // For REQUEST type, use requestName
      if (notification.objCode === 'REQUEST' && notification.objectDetails.requestName) {
        return notification.objectDetails.requestName;
      }
      // For UPDATES type, use updateName
      if (notification.objCode === 'UPDATES' && notification.objectDetails.updateName) {
        return notification.objectDetails.updateName;
      }
      // For ISSUE type, use issue title/name (never bare "ISSUE")
      if (notification.objCode === 'ISSUE') {
        const issueName =
          notification.objectDetails.issueName ||
          notification.objectDetails.name ||
          notification.objectDetails.title ||
          notification.objectname;
        if (issueName && !this.isBareObjCode(issueName, notification.objCode)) {
          return issueName;
        }
      }
      // Fallback to name or title from objectDetails
      if (notification.objectDetails.name && !this.isBareObjCode(notification.objectDetails.name, notification.objCode)) {
        return notification.objectDetails.name;
      }
      if (notification.objectDetails.title && !this.isBareObjCode(notification.objectDetails.title, notification.objCode)) {
        return notification.objectDetails.title;
      }
    }
    // Prefer mapped objectname, then project — skip bare objCode values like "ISSUE"
    const objectname = String(notification.objectname || '').trim();
    if (objectname && !this.isBareObjCode(objectname, notification.objCode)) {
      return objectname;
    }
    const project = String(notification.project || '').trim();
    if (project && !this.isBareObjCode(project, notification.objCode)) {
      return project;
    }
    return '';
  }

  private isBareObjCode(value: string, objCode?: string): boolean {
    const v = String(value || '').trim().toUpperCase();
    const code = String(objCode || '').trim().toUpperCase();
    return !!v && !!code && v === code;
  }

  /**
   * Handle notification click - route to the respective page
   */
   onNotificationClick(notification: Notification, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }

    // Get route based on notification object details
    const route = this.notificationService.getNotificationRoute(notification);
    
    if (route) {
      // Get fragment for scrolling to specific update/reply
      const fragment = this.notificationService.getNotificationFragment(notification);
      
      // Navigate with fragment if available
      if (fragment) {
        this.router.navigate(route, { fragment });
      } else {
        this.router.navigate(route);
      }
    }
  }
}
